import { Button, Spin } from "antd";
import { createRef, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AdminContext, Chat as ChatType } from "context/adminContext";
import chatIcon from "assets/svg/ChatIcon.svg";

import styles from "./Chat.module.css";
import InfiniteScroll from "react-infinite-scroll-component";
import { UploadFilesComponent } from "Components/UploadFilesComponent";
import { PaperClipIcon } from "Common/Icons/PaperClip";
import { postFileChat } from "helpers/backend_helper";
import { ChatsContext } from "context/chatsContext";
import { OtherMessage, SupportMessage, SYSTEM_MESSAGES, SystemMessage, UserMessage } from "./ChatMessagesComponents";
import { CustomTextArea } from "Components/CustomTextArea";
import { ChatHistoryReloadContext } from "Components/ChatFiles/useChatFileDownload";
// import { CustomTextArea } from "Components/CustomTextArea";

export type Identifiers = {
  chatId: string;
  cursor: number;
};

export type Handlers = {
  sendSocketMessage: (content: string, chatId: string) => Promise<any>;
  loadMore: (cursor: number) => void;
  // Свежие ссылки на файлы: по умолчанию первая страница истории
  reloadHistory?: () => void;
};

export type UIFlags = {
  loading: boolean;
  hasMore: boolean;
};

export type DataProps = {
  chatData: ChatType;
};

export type ChatProps = {
  ids: Identifiers;
  handlers: Handlers;
  flags: UIFlags;
  data: DataProps;
};

export const ChatDisput = ({ data, flags, handlers, ids }: ChatProps) => {
  const { chatData } = data;
  const { hasMore, loading } = flags;
  const { loadMore, sendSocketMessage } = handlers;
  const { chatId, cursor } = ids;

  const [textMessage, setTextMessage] = useState("");
  const divRef = createRef<HTMLDivElement>();
  const { localCountry } = useContext(AdminContext);
  const { bunchFiles, resetInputFields, fileList, setFileList } = useContext(ChatsContext);
  const sendUploadedFiles = useCallback(async () => {
    if (bunchFiles.status === "DONE") {
      if (bunchFiles.files.length) {
        const result = await postFileChat(chatId, { files: bunchFiles.files }, true, {
          headers: {
            "country-iso-code": localCountry.iso,
          },
        });
        console.log("Result of sending files: ", result);
        resetInputFields();
      }
    }
  }, [bunchFiles, chatId, localCountry.iso, resetInputFields]);

  const sendTextHandler = () => {
    if (!textMessage?.trim()) {
      sendUploadedFiles();
      return;
    }
    if (textMessage?.trim()) {
      sendSocketMessage(textMessage, chatId).then(sendUploadedFiles);
      setTextMessage("");
    }
  };

  const loadMoreData = () => {
    if (loading) return;
    loadMore(cursor);
  };

  useEffect(() => {
    if (chatId) {
      divRef.current?.scrollTo({ left: 0, top: 0 });
    }
  }, [chatId]);
  const uploadRef = useRef<any>();

  // Ссылки на файлы живут 15 минут — при истечении берём свежие из истории
  const reloadHistory = handlers.reloadHistory ?? (() => loadMore(0));

  return (
    <ChatHistoryReloadContext.Provider value={reloadHistory}>
      <div className={styles.chatWrapper}>
        <div className={styles.wrapper}>
          <div className={styles.chat} id="scrollableElem" ref={divRef}>
            <InfiniteScroll
              dataLength={chatData.messages.length}
              hasMore={hasMore}
              className={styles.chat}
              loader={<Spin />}
              inverse={true}
              next={loadMoreData}
              scrollableTarget="scrollableElem"
            >
              {!chatData.messages?.length ? (
                <div className={styles.noMessages}>
                  <img src={chatIcon} alt="chat icon" />
                  <div>Enter your first message below</div>
                </div>
              ) : null}
              {chatData.messages?.map((messageGroup) => {
                const message = messageGroup?.messages;
                const isFiles = message[0].contentType !== "text";

                if (message[0].messageType === "SYSTEM" && SYSTEM_MESSAGES[message[0].messageKey]) {
                  return (
                    <SystemMessage
                      key={messageGroup.grouping_key}
                      messageId={message[0].id}
                      messageKey={message[0].messageKey}
                      createdAt={message[0].createdAt}
                      params={message[0].messageParams}
                    />
                  );
                }

                if (
                  !message[0].admin &&
                  (message[0].authorType === "CLIENT" ||
                    message[0].author ||
                    (message[0].chatType === "RENT" && message[0].authorId))
                ) {
                  return (
                    <UserMessage
                      key={messageGroup.grouping_key}
                      content={message}
                      isFiles={isFiles}
                      messageCreatedAt={message[0].createdAt}
                      messageId={messageGroup.messages[0].id}
                      avatar={message[0]?.author?.avatar}
                    />
                  );
                } else if (message[0].authorType === "SUPPORT" || message[0].admin) {
                  return (
                    <SupportMessage
                      content={message}
                      isFiles={isFiles}
                      messageCreatedAt={message[0].createdAt}
                      key={messageGroup.grouping_key}
                      messageId={messageGroup.messages[0].id}
                    />
                  );
                } else {
                  return (
                    <OtherMessage
                      key={messageGroup.grouping_key}
                      messageId={messageGroup.messages[0].id}
                      messageCreatedAt={message[0].createdAt}
                      isFiles={isFiles}
                      content={message}
                    />
                  );
                }
              })}
            </InfiniteScroll>
          </div>

          <div className={styles.inputBox}>
            <UploadFilesComponent
              fileList={fileList}
              setFileList={setFileList}
              chatId={chatId}
              isArbitr={true}
              uploadRef={uploadRef}
            ></UploadFilesComponent>
            <Button
              type="link"
              className={styles.paperClipBtn}
              icon={<PaperClipIcon />}
              onClick={() => uploadRef.current?.upload.uploader.fileInput.click()}
            />
            <CustomTextArea
              value={textMessage}
              onChange={(e) => setTextMessage(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  sendTextHandler();
                  event.preventDefault();
                  event.stopPropagation();
                }
              }}
              isMessage
              style={
                fileList.length ? { borderTopLeftRadius: 0, borderTopRightRadius: 0, borderTopWidth: 0 } : undefined
              }
            />
            <button className={styles.sendButton} onClick={sendTextHandler} />
          </div>
        </div>
      </div>
    </ChatHistoryReloadContext.Provider>
  );
};
